import { useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash-es';
import { SCROLL_MAP } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import {
  TRADE_IN_APPRAISER_NAME,
  TRADE_IN_APPRAISER_HEADER,
  REPORT,
} from 'site-modules/shared/constants/appraisal/appraisal';

const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ', // Space key
]);
export const SCROLL_LOCK_TIMEOUT = 1500;

export function useAppraiserScroll({ isExtendedReport, isMobile, getActiveStep }) {
  const scrollCompletionCleanupRef = useRef(null);
  const scrollLockCleanupRef = useRef(null);
  const enableUserScroll = useCallback(() => {
    scrollLockCleanupRef.current?.();
    scrollLockCleanupRef.current = null;
  }, []);

  const disableUserScroll = useCallback(() => {
    enableUserScroll();

    const preventUserScroll = event => {
      // Preserve pinch and wheel zoom while preventing gestures that interrupt the step scroll.
      if ((event.type === 'touchmove' && event.touches?.length > 1) || (event.type === 'wheel' && event.ctrlKey)) {
        return;
      }
      event.preventDefault();
    };
    // The wheel and touchmove listeners don’t cover keyboard scrolling, so
    // without it keyboard keys could move the page during animations.
    const preventKeyboardScroll = event => {
      if (
        !SCROLL_KEYS.has(event.key) ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.target.closest?.('input, textarea, select, [contenteditable], [role="textbox"]') ||
        (event.key === ' ' && event.target.closest?.('button, [role="button"]'))
      ) {
        return;
      }
      event.preventDefault();
    };

    window.addEventListener('touchmove', preventUserScroll, { passive: false, capture: true });
    window.addEventListener('wheel', preventUserScroll, { passive: false, capture: true });
    window.addEventListener('keydown', preventKeyboardScroll, true);
    const fallbackTimerId = setTimeout(enableUserScroll, SCROLL_LOCK_TIMEOUT);
    scrollLockCleanupRef.current = () => {
      clearTimeout(fallbackTimerId);
      window.removeEventListener('touchmove', preventUserScroll, true);
      window.removeEventListener('wheel', preventUserScroll, true);
      window.removeEventListener('keydown', preventKeyboardScroll, true);
    };
  }, [enableUserScroll]);

  const scrollTo = useCallback(
    (selector, scrollIntoViewOptions) => {
      if (document) {
        const elem = document.querySelector(selector);
        if (elem) {
          elem.scrollIntoView({
            behavior: isExtendedReport ? 'auto' : 'smooth',
            block: 'start',
            ...scrollIntoViewOptions,
          });
        }
      }
    },
    [isExtendedReport]
  );

  const scrollToNextStep = useCallback(() => {
    const activeStep = getActiveStep();
    scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${activeStep.id}`);
  }, [getActiveStep, scrollTo]);

  const scrollToReportStep = useCallback(() => {
    setTimeout(() => {
      scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${REPORT}`);
    }, 1000);
  }, [scrollTo]);

  const scrollToTradeInTitle = useCallback(() => {
    if (isMobile) {
      scrollTo(`[name="${TRADE_IN_APPRAISER_HEADER}"]`);
    }
  }, [isMobile, scrollTo]);

  const scrollToInvalidWithMap = useCallback(
    errorMap => {
      for (let i = 0; i < SCROLL_MAP.length; i += 1) {
        const scrollObj = SCROLL_MAP[i];
        if (errorMap.has(scrollObj.input)) {
          scrollTo(`.${TRADE_IN_APPRAISER_NAME} ${scrollObj.selector}`, scrollObj.options);
          return;
        }
      }
    },
    [scrollTo]
  );

  const debouncedScrollToStep = useMemo(
    () => debounce(stepId => scrollTo(`.${TRADE_IN_APPRAISER_NAME} .${stepId}`), 1),
    [scrollTo]
  );

  const smoothScrollToStep = useCallback((stepId, onScrollComplete) => {
    scrollCompletionCleanupRef.current?.();
    scrollCompletionCleanupRef.current = null;

    const elem = document.querySelector(`.${TRADE_IN_APPRAISER_NAME} .${stepId}`);
    if (!elem) {
      onScrollComplete?.();
      return;
    }

    const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const behavior = prefersReducedMotion ? 'auto' : 'smooth';
    elem.scrollIntoView({ behavior, block: 'start' });

    if (!onScrollComplete) {
      return;
    }
    if (behavior !== 'smooth' || Math.abs(elem.getBoundingClientRect().top) <= 1) {
      onScrollComplete();
      return;
    }

    let fallbackTimerId;
    let handleScrollEnd;
    const finish = () => {
      window.removeEventListener('scrollend', handleScrollEnd);
      clearTimeout(fallbackTimerId);
      scrollCompletionCleanupRef.current = null;
      onScrollComplete();
    };

    // A scrollend from an earlier scroll must not open the step before its header is aligned.
    handleScrollEnd = () => {
      if (Math.abs(elem.getBoundingClientRect().top) <= 1) {
        finish();
      }
    };

    // User gestures can cancel scrolling before alignment; always release the incoming step.
    fallbackTimerId = setTimeout(finish, 1000);

    window.addEventListener('scrollend', handleScrollEnd);

    scrollCompletionCleanupRef.current = () => {
      window.removeEventListener('scrollend', handleScrollEnd);
      clearTimeout(fallbackTimerId);
    };
  }, []);

  // Cancel debounce when component unmounts to prevent memory leakage and unwanted scrolls
  useEffect(
    () => () => {
      debouncedScrollToStep.cancel();
      scrollCompletionCleanupRef.current?.();
      enableUserScroll();
    },
    [debouncedScrollToStep, enableUserScroll]
  );

  return {
    scrollTo,
    scrollToNextStep,
    scrollToReportStep,
    scrollToTradeInTitle,
    scrollToInvalidWithMap,
    debouncedScrollToStep,
    smoothScrollToStep,
    disableUserScroll,
    enableUserScroll,
  };
}
